import moment from "moment";

/**
 * Map recurrenceUnit to moment args for iterating periods.
 */
const recurrenceMap = {
  monthly:   { amount: 1, unit: 'months' },
  quarterly: { amount: 3, unit: 'months' },
  yearly:    { amount: 1, unit: 'years' },
};

/**
 * Estimate the total amount for a virtual account within a date range.
 */
const estimateForRange = (va, rangeStart, rangeEnd, totalFlats) => {
  const start = moment(va.startDate);
  const end = va.endDate ? moment(va.endDate) : null;
  const perFlatAmount = Number(va.amount || 0);
  if (!perFlatAmount) return 0;

  const flatMultiplier = va.billingType === 'per-flat'
    ? (va.applicability === 'selected' && Array.isArray(va.applicableFlats)
        ? va.applicableFlats.length
        : totalFlats)
    : 1;

  if (va.frequency === 'one-time') {
    // One-time: count if startDate falls within range
    if (
      start.isBetween(rangeStart, rangeEnd, undefined, '[]')
    ) {
      return perFlatAmount * flatMultiplier;
    }
    return 0;
  }

  if (va.frequency === 'recurring' && va.recurrenceUnit) {
    const rec = recurrenceMap[va.recurrenceUnit];
    if (!rec) return 0;
    let total = 0;
    const cursor = start.clone();
    const cutoff = end ? moment.min(end, rangeEnd) : rangeEnd;
    let safety = 0;
    while ((cursor.isBefore(cutoff) || cursor.isSame(cutoff, 'day')) && safety < 600) {
      if (cursor.isBetween(rangeStart, rangeEnd, undefined, '[]')) {
        total += perFlatAmount * flatMultiplier;
      }
      cursor.add(rec.amount, rec.unit);
      safety++;
    }
    return total;
  }

  return 0;
};

export const generateSummary = (accounts, primaryAccount, receivedPayments, clearedBills, summaryFilter, totalFlats) => {
  const virtualAccounts = (accounts || []).filter(a => a.accountRole !== 'primary' && a.accountRole !== 'cash');
  const rangeStart = summaryFilter.value[0];
  const rangeEnd = summaryFilter.value[1];

  const creditAccounts = virtualAccounts.filter(a => a.direction === 'credit');
  const debitAccounts = virtualAccounts.filter(a => a.direction === 'debit');

  const revenue = {
    estimated: creditAccounts.reduce((sum, va) => sum + estimateForRange(va, rangeStart, rangeEnd, totalFlats), 0),
    hasVariableCharges: creditAccounts.some(va => va.hasVariableCharges),
    generated: receivedPayments.reduce((total, each) => {
      if (moment(each.end_date).isBetween(rangeStart, rangeEnd, undefined, '[]')) {
        return total + Number(each.amount);
      }
      return total;
    }, 0),
    outstanding: 0,
    accounts: creditAccounts.reduce((total, each) => total + Number(each.amount || 0), 0),
  };

  const expense = {
    estimated: debitAccounts.reduce((sum, va) => sum + estimateForRange(va, rangeStart, rangeEnd, totalFlats), 0),
    spent: clearedBills.reduce((total, each) => {
      if (moment(each.end_date).isBetween(rangeStart, rangeEnd, undefined, '[]')) {
        return total + Number(each.amount);
      }
      return total;
    }, 0),
    pending: 0,
    accounts: debitAccounts.reduce((total, each) => total + Number(each.amount || 0), 0),
  };

  return { revenue, expense };
};
